import { capitalize } from '@CS/utils/String';
import type { AvatarAge, AvatarData } from '@/services/api/types';
import type { IAvatarOptions } from '@/components/AvatarOptionsPanel/types';

type RGB = { r: number; g: number; b: number };
export type HSL = { h: number; s: number; l: number };

const getValue = ({ r, g, b }: RGB) => (r << 16) ^ (g << 8) ^ (b << 0);

export const getInt = (color: string) => parseInt(color.slice(1), 16);

export const getHex = (color: RGB | number) => {
  color = typeof color === 'number' ? color : getValue(color);
  return `#${`000000${color.toString(16)}`.slice(-6)}`;
};

export const hslToHex = ({ h, s, l }: HSL) => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s,
    x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
    m = l - c / 2;

  let r: string | number = 0;
  let g: string | number = 0;
  let b: string | number = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255).toString(16);
  g = Math.round((g + m) * 255).toString(16);
  b = Math.round((b + m) * 255).toString(16);

  if (r.length == 1) r = '0' + r;
  if (g.length == 1) g = '0' + g;
  if (b.length == 1) b = '0' + b;

  return '#' + r + g + b;
};

export const hexToHsl = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);

  (r /= 255), (g /= 255), (b /= 255);

  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);

  let l = (max + min) / 2;

  let h = 0,
    s = 0;

  if (max == min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  h *= 360 | 0;
  s *= 100 | 0;
  l *= 100 | 0;

  return { h, s, l };
};

export enum OPTIONS {
  favoriteColor = 'favoriteColor',
  skinColor = 'skinColor',
  elements = 'elements',
  details = 'details',
  pants = 'pants',
  shirt = 'shirt',
  face = 'face',
  hair = 'hair',
  name = 'name',
  age = 'age'
}

export const TABS: string[] = [
  OPTIONS.details,
  OPTIONS.skinColor,
  OPTIONS.hair,
  OPTIONS.face,
  OPTIONS.shirt,
  OPTIONS.pants,
  OPTIONS.favoriteColor
];

enum AvatarAgeValues {
  child = 1,
  teen = 2,
  adult = 3
}

// From API:
export const mapAvatarResponse = (data: AvatarData) => ({
  [OPTIONS.name]: data.name,
  [OPTIONS.elements]: data.elements,
  [OPTIONS.skinColor]: data.skinColor,
  [OPTIONS.favoriteColor]: data.favoriteColor,

  [OPTIONS.age]:
    AvatarAgeValues[data.age.toLowerCase() as keyof typeof AvatarAgeValues]
});

// To API:
export const mapAvatarData = (data: IAvatarOptions) => ({
  [OPTIONS.name]: data.name,
  [OPTIONS.elements]: data.elements,
  [OPTIONS.skinColor]: data.skinColor,
  [OPTIONS.favoriteColor]: data.favoriteColor,
  [OPTIONS.age]: capitalize(AvatarAgeValues[+data.age]) as AvatarAge
});
